/**
 * The ideal gas law, PV = nRT, solved for any one variable with unit conversion.
 *
 * Everything is carried in SI — pascals, cubic metres, moles, kelvin — with the exact
 * molar gas constant, then the answer is converted to the unit its own menu chose.
 * Temperature must be absolute: a Celsius or Fahrenheit entry is converted first and
 * rejected if it is at or below absolute zero, because T = 0 K makes PV = 0 and the
 * other solves divide by zero.
 */

import { convert, VOLUME } from "@/lib/tool-kit/chemistry/units";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";
import {
  ATMOSPHERE,
  BAR,
  CALORIE,
  CELSIUS_OFFSET,
  GAS_CONSTANT,
} from "./constants";
import {
  fromKelvin,
  PRESSURE_UNITS,
  pressureFromPascals,
  pressureToPascals,
  toKelvin,
} from "./units";

export type GasTerm = "p" | "v" | "n" | "t";

export const GAS_TERMS: readonly GasTerm[] = ["p", "v", "n", "t"];

const AMOUNT_UNITS: Record<string, number> = { mmol: 1e-3, mol: 1, kmol: 1e3 };

export interface GasInput {
  unknown: GasTerm;
  text: Record<GasTerm, string>;
  unit: Record<GasTerm, string>;
}

export type GasFailure =
  | { ok: false; reason: "missing" }
  | {
      ok: false;
      reason: "unreadable" | "nonPositive" | "belowZero";
      term: GasTerm;
    };

export interface GasSolution {
  ok: true;
  unknown: GasTerm;
  /** All four variables in SI: Pa, m³, mol, K. */
  si: Record<GasTerm, number>;
  /** The unknown in the unit its menu chose. */
  answer: number;
  answerUnit: string;
  /** V ÷ n in litres per mole. */
  molarVolume: number;
}

export type GasOutcome = GasFailure | GasSolution;

/** SI value of a typed entry, or the reason it cannot be used. */
function readTerm(
  term: GasTerm,
  text: string,
  unit: string
): { value: number } | GasFailure {
  if (text.trim() === "") {
    return { ok: false, reason: "missing" };
  }
  const typed = parseMeasuredNumber(text);
  if (typed === null) {
    return { ok: false, reason: "unreadable", term };
  }
  if (term === "t") {
    const kelvin = toKelvin(typed, unit);
    return kelvin > 0
      ? { value: kelvin }
      : { ok: false, reason: "belowZero", term };
  }
  if (!(typed > 0)) {
    return { ok: false, reason: "nonPositive", term };
  }
  if (term === "p") {
    return { value: pressureToPascals(typed, unit) };
  }
  if (term === "v") {
    return { value: convert(typed, VOLUME, unit, "m3") };
  }
  return { value: typed * (AMOUNT_UNITS[unit] ?? 1) };
}

/** PV = nRT rearranged for `unknown`, from the other three in SI. */
export function solveIdealGas(
  unknown: GasTerm,
  known: Record<GasTerm, number>
): number {
  const { p, v, n, t } = known;
  if (unknown === "p") {
    return (n * GAS_CONSTANT * t) / v;
  }
  if (unknown === "v") {
    return (n * GAS_CONSTANT * t) / p;
  }
  if (unknown === "n") {
    return (p * v) / (GAS_CONSTANT * t);
  }
  return (p * v) / (n * GAS_CONSTANT);
}

function answerIn(unknown: GasTerm, value: number, unit: string): number {
  if (unknown === "p") {
    return pressureFromPascals(value, unit);
  }
  if (unknown === "v") {
    return convert(value, VOLUME, "m3", unit);
  }
  if (unknown === "n") {
    return value / (AMOUNT_UNITS[unit] ?? 1);
  }
  return fromKelvin(value, unit);
}

export function idealGas(input: GasInput): GasOutcome {
  const si: Record<GasTerm, number> = { p: 0, v: 0, n: 0, t: 0 };
  // Report a blank only after every typed entry has been checked, so a bad number is
  // flagged even while another field is still empty.
  let missing = false;
  for (const term of GAS_TERMS) {
    if (term === input.unknown) {
      continue;
    }
    const read = readTerm(term, input.text[term], input.unit[term]);
    if ("ok" in read) {
      if (read.reason === "missing") {
        missing = true;
      } else {
        return read;
      }
    } else {
      si[term] = read.value;
    }
  }
  if (missing) {
    return { ok: false, reason: "missing" };
  }
  const value = solveIdealGas(input.unknown, si);
  si[input.unknown] = value;
  const unit = input.unit[input.unknown];
  return {
    ok: true,
    unknown: input.unknown,
    si,
    answer: answerIn(input.unknown, value, unit),
    answerUnit: unit,
    molarVolume: (si.v / si.n) * 1000,
  };
}

/* ------------------------------------------------ reference conditions */

export interface GasConditions {
  id: "stp" | "stpLegacy" | "satp" | "satpLegacy";
  /** Kelvin. */
  t: number;
  /** Pascals. */
  p: number;
}

/**
 * Reference conditions for gases. IUPAC's current standard (since 1982) is 273.15 K and
 * 10⁵ Pa; before 1982 the pressure was 1 atm = 101 325 Pa. "SATP" is a textbook name
 * rather than an IUPAC term: 298.15 K with 1 bar (or, in older books, 1 atm).
 */
export const GAS_CONDITIONS: readonly GasConditions[] = [
  { id: "stp", t: CELSIUS_OFFSET, p: BAR },
  { id: "stpLegacy", t: CELSIUS_OFFSET, p: ATMOSPHERE },
  { id: "satp", t: 298.15, p: BAR },
  { id: "satpLegacy", t: 298.15, p: ATMOSPHERE },
];

/** Molar volume of an ideal gas, L/mol, at T kelvin and P pascals. */
export function molarVolumeLitres(t: number, p: number): number {
  return (GAS_CONSTANT * t * 1000) / p;
}

/* ------------------------------------------------------ R in every unit */

export interface GasConstantRow {
  id: string;
  /** The unit as written, e.g. "L·atm/(mol·K)". */
  unit: string;
  value: number;
}

const MMHG = PRESSURE_UNITS.find((u) => u.id === "mmHg")?.pascals ?? 1;
const TORR = PRESSURE_UNITS.find((u) => u.id === "torr")?.pascals ?? 1;

/** R in the units gas-law problems use, all derived from the exact SI value. */
export function gasConstantRows(): GasConstantRow[] {
  return [
    { id: "si", unit: "J/(mol·K)", value: GAS_CONSTANT },
    { id: "kpa", unit: "L·kPa/(mol·K)", value: GAS_CONSTANT },
    { id: "bar", unit: "L·bar/(mol·K)", value: GAS_CONSTANT / 100 },
    {
      id: "atm",
      unit: "L·atm/(mol·K)",
      value: GAS_CONSTANT / (ATMOSPHERE / 1000),
    },
    {
      id: "mmhg",
      unit: "L·mmHg/(mol·K)",
      value: (GAS_CONSTANT * 1000) / MMHG,
    },
    {
      id: "torr",
      unit: "L·Torr/(mol·K)",
      value: (GAS_CONSTANT * 1000) / TORR,
    },
    { id: "cal", unit: "cal/(mol·K)", value: GAS_CONSTANT / CALORIE },
  ];
}

/* ------------------------------------------------------------- menus */

export interface UnitOption {
  value: string;
  label: string;
}

const VOLUME_CHOICES = ["mL", "L", "m3", "cm3", "ft3", "gal"] as const;

export const GAS_UNIT_OPTIONS: Record<GasTerm, UnitOption[]> = {
  p: PRESSURE_UNITS.map((u) => ({ value: u.id, label: u.symbol })),
  v: VOLUME_CHOICES.map((id) => ({
    value: id,
    label: VOLUME.units.find((u) => u.id === id)?.symbol ?? id,
  })),
  n: [
    { value: "mmol", label: "mmol" },
    { value: "mol", label: "mol" },
    { value: "kmol", label: "kmol" },
  ],
  t: [
    { value: "K", label: "K" },
    { value: "C", label: "°C" },
    { value: "F", label: "°F" },
  ],
};

/** The SI unit each variable is calculated in, and its menu id. */
export const GAS_SI_UNITS: Record<GasTerm, { id: string; symbol: string }> = {
  p: { id: "Pa", symbol: "Pa" },
  v: { id: "m3", symbol: "m³" },
  n: { id: "mol", symbol: "mol" },
  t: { id: "K", symbol: "K" },
};

export function gasUnitSymbol(term: GasTerm, id: string): string {
  return GAS_UNIT_OPTIONS[term].find((o) => o.value === id)?.label ?? id;
}

/** Above this pressure an ideal-gas answer is only a rough estimate. */
export const HIGH_PRESSURE_PA = 10 * ATMOSPHERE;
